import BigNumber from 'bignumber.js'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import IconOrderArrow from '~/assets/images/order-arrow.svg'
import Tooltip from '~/components/Tooltip'
import { useToken } from '~/hooks/useToken'
import { LimitOrderItem, LimitOrderStatus } from '~/hooks/useUserLimitOrderList'
import { FixedPointNumber } from '~/utils/fixedPoint'
import {
  CancelButton,
  OrderNote,
  OrderStatus,
  Row,
  StyledOrder,
  StyledTokens,
} from './styled'
import { useLimitOrderCancel } from '~/hooks/useLimitOrderCancel'
import { formatUnits } from 'ethers/lib/utils'
import { LOCALES } from '~/locales'
import { useTokenBalance } from '~/state/wallet/hooks'
import { useMemo } from 'react'
import { useSwitch, useUpdaterChainId } from '~/hooks'
import { useLimitSwapTokens } from '~/state/dex/hooks'
import useSWR from 'swr'
import { getMarketPriceHistoryChartLine } from '~/apis'
import {
  Mainnet,
  Sepolia,
  ArbitrumOne,
  ArbitrumRinkeby,
} from '~/model/chain/ethereum'
import { useLimitOrderAllowance } from '../../hooks/useLimitOrderAllowance'
import { useEstimatedExecutionPrice } from '../../hooks/useEstimatedExecutionPrice'
import { useTokenPrice } from '~/hooks/useTokenPrice'
import { getSpreadTextLite } from '../SpreadLabel'
import IconQuestionMark from '~/assets/images/question-mark-gray.svg'

interface OrderProps {
  order: LimitOrderItem
}

const historyPriceDaysRange: number[] = [1, 7, 30, 365]

const Order: React.FC<OrderProps> = ({ order }) => {
  const { t, i18n } = useTranslation()
  const timeFormat =
    i18n.resolvedLanguage === LOCALES.ZH
      ? 'YYYY-MM-DD HH:mm:ss'
      : 'DD/MM/YYYY HH:mm:ss'

  const {
    expiry,
    createdAt,
    cancelledAt,
    updatedAt,
    status,
    takerToken: outputTokenAddr,
    makerToken: inputTokenAddr,
    takerTokenAmount: outputTokenAmount = 0,
    makerTokenAmount: inputTokenAmount = 0,
    remainInputAmountToDeal,
    progress,
  } = order
  const outputTokenOnChain = useToken(outputTokenAddr)
  const inputTokenOnChain = useToken(inputTokenAddr)
  const expectChainId = useUpdaterChainId()
  const limitSwapTokens = useLimitSwapTokens(expectChainId)
  const inputToken = useMemo(() => {
    return inputTokenOnChain
      ? inputTokenOnChain
      : limitSwapTokens[inputTokenAddr]
  }, [inputTokenOnChain, limitSwapTokens])
  const outputToken = useMemo(() => {
    return outputTokenOnChain
      ? outputTokenOnChain
      : limitSwapTokens[outputTokenAddr]
  }, [outputTokenOnChain, limitSwapTokens])
  const inputPrice = useTokenPrice(inputToken?.address)
  const outputPrice = useTokenPrice(outputToken?.address)
  const cancelOrder = useLimitOrderCancel()
  const userInputBalance = useTokenBalance(inputToken)
  const isInputBalanceEnough = useMemo(() => {
    if (!userInputBalance || userInputBalance.isZero()) return false
    return userInputBalance.gte(remainInputAmountToDeal || 0)
  }, [userInputBalance, remainInputAmountToDeal])

  const allowance = useLimitOrderAllowance(inputToken)
  const isInputAllowanceEnough = useMemo(() => {
    if (!allowance || allowance.isZero()) return false
    return allowance.gte(remainInputAmountToDeal || 0)
  }, [allowance, remainInputAmountToDeal])

  const outputAmount = formatUnits(
    outputTokenAmount,
    outputToken?.decimals,
  ).toString()
  const inputAmount = formatUnits(
    inputTokenAmount,
    inputToken?.decimals,
  ).toString()
  const rate = new FixedPointNumber(
    new BigNumber(outputAmount).div(inputAmount),
  ).toSignificant(4)
  const reversedRate = new FixedPointNumber(
    new BigNumber(inputAmount).div(outputAmount),
  ).toSignificant(4)
  const { sw: reversed, toggle } = useSwitch(false)
  const leftToken = reversed ? outputToken : inputToken
  const rightToken = reversed ? inputToken : outputToken
  const leftTokenPrice = reversed ? outputPrice : inputPrice
  const rightTokenPrice = reversed ? inputPrice : outputPrice
  const displayRate = useMemo(() => (reversed ? reversedRate : rate), [
    reversed,
    rate,
    reversedRate,
  ])
  const rateUSDLiteral = useMemo(() => {
    const usd = new BigNumber(displayRate)
      .multipliedBy(rightTokenPrice)
      .toFixed(2)
    return Number(usd) ? `(~$${usd})` : '(~)'
  }, [displayRate, rightTokenPrice])
  const rateLiteral = useMemo(
    () =>
      `1 ${leftToken?.symbol} = ${displayRate} ${rightToken?.symbol} ${rateUSDLiteral}`,
    [displayRate, leftToken, rightToken, rateUSDLiteral],
  )

  const formattedRemainInputAmount = formatUnits(
    remainInputAmountToDeal,
    inputToken?.decimals,
  ).toString()
  const formattedRemainOutputAmount = useMemo(() => {
    const rate = new BigNumber(outputAmount).div(inputAmount)
    return new BigNumber(formattedRemainInputAmount)
      .multipliedBy(rate)
      .toString()
  }, [inputAmount, outputAmount, formattedRemainInputAmount])
  const getExecutionRate = useEstimatedExecutionPrice(
    inputToken,
    formattedRemainInputAmount,
    formattedRemainOutputAmount,
  )
  const executionRate = getExecutionRate()
  const reversedExecutionRate = useMemo(() => {
    const rate = new BigNumber(1).div(executionRate).sd(4).toString()
    return Number(rate) ? rate : '~'
  }, [executionRate])
  const displayExecutionRate = reversed ? reversedExecutionRate : executionRate
  // 小额订单不足以支付矿工费，有可能算出负数
  const isCorrectExecutionRate = Number(displayExecutionRate) > 0

  const executionRateUSDLiteral = useMemo(() => {
    const usd = new BigNumber(displayExecutionRate)
      .multipliedBy(rightTokenPrice)
      .toFixed(2)
    return Number(usd) ? `(~$${usd})` : '(~)'
  }, [reversed, displayExecutionRate, rightTokenPrice])

  const executionRateLiteral = useMemo(
    () =>
      `1 ${leftToken?.symbol} ≈ ${displayExecutionRate} ${rightToken?.symbol} ${executionRateUSDLiteral}`,
    [leftToken, rightToken, displayExecutionRate, executionRateUSDLiteral],
  )

  const marketRate = useMemo(() => {
    const rate = new BigNumber(leftTokenPrice)
      .div(rightTokenPrice)
      .sd(4)
      .toString()
    return Number(rate) ? rate : '~'
  }, [leftTokenPrice, rightTokenPrice])

  const executionRateUSDSpread = useMemo(() => {
    if (!Number(displayExecutionRate) || !Number(marketRate)) return '0'
    return new BigNumber(displayExecutionRate)
      .div(marketRate)
      .minus(1)
      .times(100)
      .toFixed(2)
  }, [displayExecutionRate, marketRate])

  const executionRateUSDSpreadLiteral = getSpreadTextLite(
    executionRateUSDSpread,
  )

  const executionRateTooltipLiteral = useMemo(() => {
    if (Number(executionRateUSDSpread) === 0)
      return `1 ${leftToken?.symbol} ≈ ${displayExecutionRate} ${rightToken?.symbol}`
    return `1 ${leftToken?.symbol} ≈ ${displayExecutionRate} ${rightToken?.symbol} (${executionRateUSDSpreadLiteral})`
  }, [
    leftToken,
    rightToken,
    displayExecutionRate,
    executionRateUSDSpread,
    executionRateUSDSpreadLiteral,
  ])

  const now = dayjs()
  const createdTime = dayjs(createdAt)
  const diffDays = now.diff(createdTime, 'day', true)
  const days = Math.ceil(diffDays)

  const selectedDate = historyPriceDaysRange.find((d) => d >= days)

  const isSupportedChains = [
    Mainnet.chainId,
    Sepolia.chainId,
    ArbitrumOne.chainId,
    ArbitrumRinkeby.chainId,
  ].includes(expectChainId)

  const isNotCompleted = [
    LimitOrderStatus.PartialFilled,
    LimitOrderStatus.Pending,
  ].includes(status)

  const isPendingOrder = status === LimitOrderStatus.Pending

  const { data: historyPrice } = useSWR(
    isSupportedChains && isNotCompleted
      ? [
          'getMarketPriceHistoryChartLine',
          expectChainId,
          selectedDate,
          inputToken?.address,
          outputToken?.address,
        ]
      : null,
    () =>
      getMarketPriceHistoryChartLine(
        selectedDate.toString(),
        inputToken?.symbol,
        outputToken?.symbol,
      ),
    { refreshInterval: 1000 * 60 * 15 },
  )

  const createdTimestamp = new Date(createdAt).getTime()
  const haveReached = historyPrice?.find((item) => {
    if (item.timestamp >= createdTimestamp) {
      return item.price >= Number(rate)
    }
    return false
  })

  return (
    <StyledOrder>
      <StyledTokens>
        <div>
          {inputAmount} {inputToken?.symbol}
          <img src={IconOrderArrow} />
          {outputAmount} {outputToken?.symbol}
        </div>
        <OrderStatus className={status}>
          {t(`limit_order_status_${status}`)}
        </OrderStatus>
      </StyledTokens>
      {isPendingOrder && !!haveReached && isSupportedChains && (
        <OrderNote>{t('lo_order_pending_tip')}</OrderNote>
      )}
      {isNotCompleted && !isInputBalanceEnough && (
        <OrderNote>
          {t('order_insufficient_balance', { symbol: inputToken?.symbol })}
        </OrderNote>
      )}
      {isNotCompleted && !isInputAllowanceEnough && (
        <OrderNote>
          {t('order_insufficient_allowance', { symbol: inputToken?.symbol })}
        </OrderNote>
      )}
      {status === LimitOrderStatus.Cancelled &&
      [1657004700, 1714486200].includes(dayjs(cancelledAt).unix()) ? (
        <OrderNote>{t('order_in_deprecated_contract')}</OrderNote>
      ) : null}
      <Row>
        <div className="label">{t('limit_price')}</div>
        <div className="value" onClick={toggle}>
          {rateLiteral}
        </div>
      </Row>
      {isNotCompleted && (
        <>
          {isCorrectExecutionRate && (
            <Row>
              <div className="label">
                {t('est_execute_price')}
                <Tooltip
                  placement="right"
                  title={t('est_execute_price_tip', {
                    executionRate: executionRateTooltipLiteral,
                  })}
                  trigger="hover"
                  img={IconQuestionMark}
                />
              </div>
              <div className="value" onClick={toggle}>
                {executionRateLiteral}
              </div>
            </Row>
          )}
        </>
      )}
      <Row>
        <div className="label">{t('placed_time')}</div>
        <div className="value">{dayjs(createdAt).format(timeFormat)}</div>
      </Row>
      {status === LimitOrderStatus.Fulfilled ? (
        <Row>
          <div className="label">{t('deal_time')}</div>
          <div className="value">{dayjs(updatedAt).format(timeFormat)}</div>
        </Row>
      ) : status === LimitOrderStatus.Cancelled ? (
        <Row>
          <div className="label">{t('cancelled_time')}</div>
          <div className="value">{dayjs(cancelledAt).format(timeFormat)}</div>
        </Row>
      ) : (
        <Row>
          <div className="label">{t('expired_time')}</div>
          <div className="value">{dayjs.unix(expiry).format(timeFormat)}</div>
        </Row>
      )}
      <Row>
        <div className="label">
          {t('order_processed')}
          <Tooltip
            placement="right"
            title={t('order_processed_tip')}
            trigger="hover"
            img={IconQuestionMark}
          />
        </div>
        <div className="value">{Number(progress)}%</div>
      </Row>
      {(status === LimitOrderStatus.Pending ||
        status === LimitOrderStatus.PartialFilled) && (
        <Row>
          <Tooltip placement="top" title={t('cancel_order')} trigger="hover">
            <CancelButton onClick={() => cancelOrder(order)}>
              {t('cancel')}
            </CancelButton>
          </Tooltip>
        </Row>
      )}
    </StyledOrder>
  )
}

export default Order
